import React from "react";
import { Field, useFormikContext } from "formik";
import { Col, Form, Row } from "react-bootstrap";
import { AddressFormSection, AddressLookupProvider, AddressPanel } from "../../components/UI";
import { RequiredFieldMarker } from "../../components/Forms/RequiredFieldMarker";
import { useViewport } from "../../contexts";
import type { ISiteOption } from "../../customTypings/processor/Processor";
import { shouldShowFormFieldError } from "../../utils";

export interface ProcessorEditFormGridProps {
  showSiteField: boolean;
  isNew: boolean;
  metaDeleted: boolean;
  siteId: string;
  setSiteId: (siteId: string) => void;
  siteDisplayName: string;
  sites: ISiteOption[];
  liveValidationAfterSubmit: boolean;
  errors: { name?: string; environmentalPermitNumber?: string };
  touched: { name?: boolean; environmentalPermitNumber?: boolean };
}

function GridFieldCell({ children }: { children: React.ReactNode }) {
  return <div className="mb-3">{children}</div>;
}

function ProcessorSiteField({
  showSiteField,
  isNew,
  metaDeleted,
  siteId,
  setSiteId,
  siteDisplayName,
  sites,
}: Pick<
  ProcessorEditFormGridProps,
  "showSiteField" | "isNew" | "metaDeleted" | "siteId" | "setSiteId" | "siteDisplayName" | "sites"
>) {
  if (!showSiteField) return null;

  return isNew ? (
    <Form.Group>
      <Form.Label>Site <RequiredFieldMarker /></Form.Label>
      <Form.Select
        value={siteId}
        onChange={(e) => setSiteId(e.target.value)}
        disabled={metaDeleted}
        aria-label="Site"
        required
      >
        <option value="">Select a site…</option>
        {sites.map((s) => (
          <option key={s.id} value={s.id}>
            {s.siteName}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  ) : (
    <Form.Group>
      <Form.Label>Site</Form.Label>
      <Form.Control readOnly disabled value={siteDisplayName} />
    </Form.Group>
  );
}

function ProcessorNameField({
  metaDeleted,
  liveValidationAfterSubmit,
  errors,
  touched,
}: Pick<ProcessorEditFormGridProps, "metaDeleted" | "liveValidationAfterSubmit" | "errors" | "touched">) {
  return (
    <Form.Group>
      <Form.Label>Name <RequiredFieldMarker /></Form.Label>
      <Field
        name="name"
        className={`form-control${shouldShowFormFieldError("name", errors, touched, liveValidationAfterSubmit) ? " is-invalid" : ""}`}
        disabled={metaDeleted}
      />
      {shouldShowFormFieldError("name", errors, touched, liveValidationAfterSubmit) && (
        <div className="invalid-feedback d-block">{errors.name}</div>
      )}
    </Form.Group>
  );
}

function ProcessorEnvironmentalPermitField({
  metaDeleted,
  liveValidationAfterSubmit,
  errors,
  touched,
}: Pick<ProcessorEditFormGridProps, "metaDeleted" | "liveValidationAfterSubmit" | "errors" | "touched">) {
  const { setFieldValue } = useFormikContext();

  return (
    <Form.Group>
      <Form.Label htmlFor="environmentalPermitNumber">Environmental permit number</Form.Label>
      <Field
        id="environmentalPermitNumber"
        name="environmentalPermitNumber"
        maxLength={128}
        className={`form-control${shouldShowFormFieldError("environmentalPermitNumber", errors, touched, liveValidationAfterSubmit) ? " is-invalid" : ""}`}
        disabled={metaDeleted}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          void setFieldValue("environmentalPermitNumber", e.target.value);
        }}
      />
      {shouldShowFormFieldError("environmentalPermitNumber", errors, touched, liveValidationAfterSubmit) && (
        <div className="invalid-feedback d-block">{errors.environmentalPermitNumber}</div>
      )}
    </Form.Group>
  );
}

function ProcessorEnabledCheckbox() {
  return (
    <div className="form-check">
      <Field id="Processor-is-enabled" name="isEnabled" type="checkbox" className="form-check-input" />
      <label className="form-check-label" htmlFor="Processor-is-enabled">
        Enabled
      </label>
    </div>
  );
}

function ProcessorAddressPanel({
  layoutVariant,
  postcodeLayout,
  formReadOnly,
}: {
  layoutVariant: "pairedRows" | "stacked";
  postcodeLayout: "below" | "separate";
  formReadOnly: boolean;
}) {
  return (
    <AddressPanel className="mb-3">
      <AddressFormSection
        layoutVariant={layoutVariant}
        postcodeLayout={postcodeLayout}
        requiredFields={[]}
        readOnly={formReadOnly}
      />
    </AddressPanel>
  );
}

export function ProcessorEditFormGridLarge(props: ProcessorEditFormGridProps) {
  return (
    <div className="vstack gap-2">
      <Row className="g-3">
        <Col lg={4}>
          <div className="vstack gap-2">
            {props.showSiteField && (
              <GridFieldCell>
                <ProcessorSiteField {...props} />
              </GridFieldCell>
            )}
            <GridFieldCell>
              <ProcessorNameField {...props} />
            </GridFieldCell>
            <GridFieldCell>
              <ProcessorEnvironmentalPermitField {...props} />
            </GridFieldCell>
            {props.isNew && (
              <GridFieldCell>
                <ProcessorEnabledCheckbox />
              </GridFieldCell>
            )}
          </div>
        </Col>
        <Col lg={8}>
          <ProcessorAddressPanel layoutVariant="pairedRows" postcodeLayout="below" formReadOnly={props.metaDeleted} />
        </Col>
      </Row>
    </div>
  );
}

export function ProcessorEditFormGridMedium(props: ProcessorEditFormGridProps) {
  return (
    <div className="vstack gap-2">
      <Row className="g-3">
        <Col md={6}>
          <div className="vstack gap-2">
            {props.showSiteField && (
              <GridFieldCell>
                <ProcessorSiteField {...props} />
              </GridFieldCell>
            )}
            <GridFieldCell>
              <ProcessorNameField {...props} />
            </GridFieldCell>
            <GridFieldCell>
              <ProcessorEnvironmentalPermitField {...props} />
            </GridFieldCell>
            {props.isNew && (
              <GridFieldCell>
                <ProcessorEnabledCheckbox />
              </GridFieldCell>
            )}
          </div>
        </Col>
        <Col md={6}>
          <ProcessorAddressPanel layoutVariant="stacked" postcodeLayout="below" formReadOnly={props.metaDeleted} />
        </Col>
      </Row>
    </div>
  );
}

/**
 * Narrow: Site, Name, Environmental permit, then address panel, then Enabled (new only).
 */
export function ProcessorEditFormGridNarrow(props: ProcessorEditFormGridProps) {
  return (
    <>
      {props.showSiteField && (
        <div className="mb-3">
          <ProcessorSiteField {...props} />
        </div>
      )}
      <div className="mb-3">
        <ProcessorNameField {...props} />
      </div>
      <div className="mb-3">
        <ProcessorEnvironmentalPermitField {...props} />
      </div>
      <ProcessorAddressPanel layoutVariant="stacked" postcodeLayout="separate" formReadOnly={props.metaDeleted} />
      {props.isNew && (
        <div className="mb-3">
          <ProcessorEnabledCheckbox />
        </div>
      )}
    </>
  );
}

export function ProcessorEditFormGrid(props: ProcessorEditFormGridProps) {
  const { width } = useViewport();

  return (
    <AddressLookupProvider>
      {width >= 992 ? (
        <ProcessorEditFormGridLarge {...props} />
      ) : width >= 768 ? (
        <ProcessorEditFormGridMedium {...props} />
      ) : (
        <ProcessorEditFormGridNarrow {...props} />
      )}
    </AddressLookupProvider>
  );
}
